import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { logFeature, logImpression } from '@office-start/telemetry-service';
import { Area, KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { CalloutType } from '../../feature-callout/feature-callout.model';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../../shared/instrumentation/instrumented-http.service';

import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { SchedulerService } from '../../shared/services/scheduler.service';
import { UserDataStorageService } from '../../shared/services/user-data-storage.service';
import { XhrService } from '../../shared/services/xhr.service';
import { ContextService } from '../../shared/shared-context/context.service';

// Keep in sync with FirstRunData.cs on server.
export interface FirstRunData {
    /*
     * This property allows us to distinguish between users who dismiss first run (and should not see it again) from users
     * who abandoned in the middle of the experience, and should be shown first run again on next visit
     */
    CompletedFirstRun: boolean;
    StepsCompleted: number;
    Version: number;
    DateShown?: number;
    TotalSteps?: number;
    FurthestCompleted?: number;
}

export interface ConsumerSubscriptionProductData {
    EndDate: Date;
    AutoRenew: boolean;
    IsTrial: boolean;
    IsOwner: boolean;
    IsFamily: boolean;
    EntitlementValidityReasonCode: string;
}

export interface ConsumerLicensingData {
    ActiveSubscriptionProduct: ConsumerSubscriptionProductData;
    ExpiredSubscriptionProduct: ConsumerSubscriptionProductData;
    PerpetualCategories: Array<string>;
}

@Injectable({
    providedIn: 'root'
})

export class FirstRunDataService extends XhrService {
    private readonly requestConfig: HttpRequestOptions = {
        headers: new Map([
            ['Accept', 'application/json']
        ])
    };

    public static readonly firstRunDataKey = 'FirstRunData';
    private static readonly LoadFromUserStorageRemoteAttempts = 2;
    private static readonly ConsumerLicenseStatusEndpoint = '/api/license/consumer';
    private static readonly ConsumerLicenseStatusForceRefreshQuery = 'forceRefresh=true';

    private alreadyCompleted: boolean = false; // Used to mark the first time we complete FRE so we don't log multiple completions after navigating to last callout

    constructor(
        accessTokenService: AccessTokenService,
        instrumentedHttp: InstrumentedHttp,
        instrumentationService: InstrumentationService,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        scheduler: SchedulerService,
        private readonly localStorageService: LocalStorageService,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OfficeHome, contextService, officeHomeApiConfigService, platformLocation, scheduler);
    }

    public fetchFirstRunData(currentVersion: number, skipRoaming: boolean = false, dataKey: string): Promise<FirstRunData> {
        return new Promise(resolve => {
            const localStorageDataKey = `_${dataKey}`;
            // Check if local storage has cached first run data
            const cachedFirstRunData: FirstRunData =
                this.localStorageService.get(localStorageDataKey) ||
                this.createInitialFirstRunData(currentVersion);

            // With skipRoaming option set to true return data immediately
            if (skipRoaming) {
                resolve(cachedFirstRunData);
                return;
            }

            let resolvedFirstRunData = false;

            // Resolve to cached data immediately if it indicates that first run was already completed
            if (cachedFirstRunData.CompletedFirstRun) {
                resolvedFirstRunData = true;
                resolve(cachedFirstRunData);
            }

            // Check roaming storage and use/update it appropriately
            this.userDataStorageService
                .get<FirstRunData>(dataKey, FirstRunDataService.LoadFromUserStorageRemoteAttempts)
                .then(roamingFirstRunData => {
                    if (roamingFirstRunData && roamingFirstRunData.CompletedFirstRun) {
                        // If data in local and roaming storage is in sync then there is nothing else to do;
                        // otherwise, resolve to data from roaming storage and put it in local storage
                        // so it is available immediately next time it is needed
                        if (!cachedFirstRunData.CompletedFirstRun) {
                            resolvedFirstRunData = true;
                            resolve(roamingFirstRunData);
                            this.localStorageService.set(localStorageDataKey, roamingFirstRunData);
                        }
                    } else {
                        // Update roaming storage if cached data indicates that first run was already completed
                        if (cachedFirstRunData.CompletedFirstRun) {
                            this.storeFirstRunDataInUserStorage(cachedFirstRunData, dataKey);
                        }
                    }
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.oZtOo);

                    // Update roaming storage if cached data indicates that first run was already completed
                    if (cachedFirstRunData.CompletedFirstRun) {
                        this.storeFirstRunDataInUserStorage(cachedFirstRunData, dataKey);
                    }
                }) // TODO: Replace with `finally()` when appropriate polyfill is added
                .then(() => {
                    // Resolve to locally cached data by default
                    if (!resolvedFirstRunData) {
                        resolvedFirstRunData = true;
                        resolve(cachedFirstRunData);
                    }
                });
        });
    }

    public updateFirstRunData(firstRunData: FirstRunData,  dataKey: string, calloutType?: CalloutType, instrument: boolean = true) {
         this.fetchFirstRunData(firstRunData.Version, false, dataKey)
            .then(firstRunState => {
                const localStorageDataKey = `_${dataKey}`;
                // if firstRunData.CompletedFirstRun is true, user took action to dismiss callouts
                const userDismissedCallout = firstRunData.CompletedFirstRun;
                if (firstRunState.CompletedFirstRun || firstRunData.StepsCompleted >= firstRunData.TotalSteps) {
                    firstRunData.CompletedFirstRun = true;
                }
                firstRunData.FurthestCompleted = firstRunState.FurthestCompleted;
                if (typeof (firstRunState.FurthestCompleted) === 'undefined' || firstRunData.StepsCompleted > firstRunState.FurthestCompleted) {
                    firstRunData.FurthestCompleted = firstRunData.StepsCompleted;
                }

                this.localStorageService.set(localStorageDataKey, firstRunData);
                this.storeFirstRunDataInUserStorage(firstRunData, dataKey);
                if (instrument) {
                    this.instrumentFirstRunData(firstRunData, calloutType, userDismissedCallout);
                }
            });
    }

    public forceRefreshAndGetLicenseStatus() {
        const pr: Promise<ConsumerLicensingData> = new Promise((resolve, reject) => {
            this.get<ConsumerLicensingData>(KnownInstrumentationEvents.OfficeConsumerLicenseFetch, KnownRequestInstrumentationEvents.OfficeConsumerLicenseFetch,
            this.contextService.sharedContext.officeHomeApiRoot + FirstRunDataService.ConsumerLicenseStatusEndpoint + '?' + FirstRunDataService.ConsumerLicenseStatusForceRefreshQuery,
            (promise: InstrumentedHttpPromise<ConsumerLicensingData>) => {
                promise.instrument((result, params) => {
                    if (result.ok) {
                        params[InstrumentationParam.HttpCode] = HttpStatusCode.OK.toString();
                    } else {
                        params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                    }
                }).then(response => {
                    resolve(response.data);
                });
            }, error => {
                this.globalErrorHandler.handleError(error, ErrorId.wCBHa);
                reject(error);
            },
            this.requestConfig,
            FetchStrategy.NetworkOnly);
        });
        return pr;
    }

    protected isRetriableError(status: number) {
        return status === HttpStatusCode.Throttled;
    }

    private createInitialFirstRunData(version: number): FirstRunData {
        return { CompletedFirstRun: false, StepsCompleted: 0, Version: version };
    }

    private storeFirstRunDataInUserStorage(firstRunData: FirstRunData, dataKey: string): void {
        this.userDataStorageService.set<FirstRunData>(dataKey, firstRunData);
    }

    private instrumentFirstRunData(firstRunData: FirstRunData, calloutType: CalloutType, userDismissedCallout: boolean) {
        const params = {};
        params[InstrumentationParam.FirstRunStepsCompleted] = firstRunData.StepsCompleted.toString();
        params[InstrumentationParam.FirstRunTotalSteps] = firstRunData.TotalSteps ? firstRunData.TotalSteps.toString() : '0';
        params[InstrumentationParam.FirstRunVersion] = firstRunData.Version.toString();
        params[InstrumentationParam.FirstRunFurthestCompleted] = firstRunData.FurthestCompleted.toString();
        params[InstrumentationParam.FirstRunUserDismissedCallout] = userDismissedCallout.toString();

        if (firstRunData.CompletedFirstRun && !this.alreadyCompleted) {
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FirstRunCompleted, params);
            // TODO: Remove above logger once OTEL migration is complete
            logFeature({
                featureName: KnownFeatureNames.FirstRun,
                eventName: KnownInstrumentationEvents.FirstRunCompleted,
                params
            });

            this.alreadyCompleted = true;
        }
        if (!firstRunData.CompletedFirstRun || typeof (calloutType) !== 'undefined') {
            // If calloutType is defined but firstRunData.CompletedFirstRun = true, we have finished FRE by
            // viewing callouts and we still want to log the last impression. If calloutType is undefined,
            // we have exited out of FRE manually so we don't log an impression.
            params[InstrumentationParam.ItemCategory] = calloutType;
            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.FirstRunImpression, params);
            // TODO: Remove above logger once OTEL migration is complete
            logImpression({
                eventName: KnownInstrumentationEvents.FirstRunImpression,
                area: calloutType as Area
            });
        }
    }
}
